import { Controller } from "@hotwired/stimulus"

// Collapses a group of checkboxes into a dropdown on narrow viewports.
//
// Progressive enhancement, same shape as the venue carousel: the panel is a
// plain visible grid until this controller adds `is-enhanced`, and the CSS only
// turns the dropdown on below the mobile breakpoint. Nothing about the form
// data changes — these stay ordinary checkboxes with the same field names, so
// multi-select keeps working and no JS is required to submit.
export default class extends Controller {
  static targets = ["toggle", "panel", "label"]
  static values = { placeholder: { type: String, default: "Select all that apply" } }

  connect() {
    this.element.classList.add("is-enhanced")
    this.refresh()

    this.outsideListener = event => {
      if (!this.element.contains(event.target)) this.close()
    }
    this.keyListener = event => {
      if (event.key === "Escape" && this.open) {
        this.close()
        this.toggleTarget.focus()
      }
    }

    document.addEventListener("click", this.outsideListener)
    document.addEventListener("keydown", this.keyListener)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideListener)
    document.removeEventListener("keydown", this.keyListener)
    this.element.classList.remove("is-enhanced", "is-open")
  }

  get open() {
    return this.element.classList.contains("is-open")
  }

  get checked() {
    return this.checkboxes.filter(box => box.checked)
  }

  get checkboxes() {
    return Array.from(this.panelTarget.querySelectorAll("input[type=checkbox]"))
  }

  toggle() {
    this.open ? this.close() : this.expand()
  }

  expand() {
    this.element.classList.add("is-open")
    this.toggleTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.element.classList.remove("is-open")
    this.toggleTarget.setAttribute("aria-expanded", "false")
  }

  // Keeps the collapsed control describing what is selected. Two names fit;
  // beyond that a count is more legible than a truncated list.
  refresh() {
    const checked = this.checked
    if (!this.hasLabelTarget) return

    if (checked.length === 0) {
      this.labelTarget.textContent = this.placeholderValue
      this.labelTarget.classList.add("is-placeholder")
      return
    }

    this.labelTarget.classList.remove("is-placeholder")
    this.labelTarget.textContent = checked.length <= 2
      ? checked.map(box => this.labelFor(box)).join(", ")
      : `${checked.length} selected`
  }

  labelFor(box) {
    const label = this.panelTarget.querySelector(`label[for="${box.id}"]`)
    return (label ? label.textContent : box.value).trim()
  }
}
